import React, { useState } from 'react';
import { Sparkles, Copy, Send, Loader, CheckCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AISalesAssistant = ({ leadId, serviceName, country, onAnswerGenerated }) => {
  const [question, setQuestion] = useState('');
  const [loading, setLoading] = useState(false);
  const [answer, setAnswer] = useState(null);
  const [error, setError] = useState(null);

  const getAuthHeader = () => ({
    'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
  });

  const askQuestion = async () => {
    if (!question.trim()) {
      setError('Please enter a question');
      return;
    }

    setLoading(true);
    setError(null);
    setAnswer(null);

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/ai-assistant/ask`,
        {
          question: question.trim(),
          service_name: serviceName,
          country: country,
          lead_id: leadId
        },
        { headers: getAuthHeader() }
      );

      setAnswer(response.data);
      if (onAnswerGenerated) {
        onAnswerGenerated(response.data);
      }
    } catch (err) {
      console.error('AI Assistant error:', err);
      setError(err.response?.data?.detail || 'Failed to get answer. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    if (answer?.answer) {
      navigator.clipboard.writeText(answer.answer);
      alert('Answer copied to clipboard!');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      askQuestion();
    }
  };

  return (
    <div className="ai-sales-assistant">
      <div className="ai-assistant-header">
        <div className="ai-header-title">
          <Sparkles size={20} color="#A08EA8" />
          <h3>AI Sales Assistant</h3>
        </div>
        <p className="ai-helper-text">Ask customer questions and get instant AI-powered answers</p>
      </div>

      <div className="ai-assistant-body">
        {/* Service Context Display */}
        {(serviceName || country) && (
          <div className="ai-context-info">
            {serviceName && <span className="context-badge">📋 {serviceName}</span>}
            {country && <span className="context-badge">🌍 {country}</span>}
          </div>
        )}

        {/* Question Input */}
        <div className="ai-question-section">
          <label>Customer Question:</label>
          <textarea
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="e.g., Can I bring my spouse and children on Canada PR?"
            rows="3"
            disabled={loading}
          />
        </div>

        {/* Action Button */}
        <button
          onClick={askQuestion}
          disabled={loading || !question.trim()}
          className="ai-ask-button"
        >
          {loading ? (
            <>
              <Loader size={16} className="spinning" />
              Getting Answer...
            </>
          ) : (
            <>
              <Sparkles size={16} />
              Get AI Answer
            </>
          )}
        </button>

        {/* Error Display */}
        {error && (
          <div className="ai-error">
            <AlertCircle size={16} />
            <span>{error}</span>
          </div>
        )}

        {/* Answer Display */}
        {answer && (
          <div className="ai-answer-section">
            <div className="ai-answer-header">
              <div className="answer-source">
                {answer.source === 'knowledge_center' ? (
                  <>
                    <CheckCircle size={16} color="#10b981" />
                    <span className="source-badge kb-badge">
                      Knowledge Base
                    </span>
                    <span className="usage-count">Used {answer.times_used} times</span>
                  </>
                ) : (
                  <>
                    <Sparkles size={16} color="#A08EA8" />
                    <span className="source-badge ai-badge">
                      AI Generated
                    </span>
                    <span className="confidence-score">
                      {answer.confidence_score}% confidence
                    </span>
                  </>
                )}
              </div>
            </div>

            <div className="ai-answer-content">
              <p>{answer.answer}</p>
            </div>

            {/* Tags */}
            {answer.tags && answer.tags.length > 0 && (
              <div className="ai-tags">
                {answer.tags.slice(0, 5).map((tag, idx) => (
                  <span key={idx} className="ai-tag">#{tag}</span>
                ))}
              </div>
            )}

            {/* Similar Questions */}
            {answer.similar_questions && answer.similar_questions.length > 0 && (
              <div className="similar-questions">
                <strong>💡 Similar Questions:</strong>
                <ul>
                  {answer.similar_questions.map((q, idx) => (
                    <li key={idx} onClick={() => setQuestion(q)}>{q}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Actions */}
            <div className="ai-answer-actions">
              <button onClick={copyToClipboard} className="btn-secondary">
                <Copy size={14} />
                Copy Answer
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AISalesAssistant;
